/**
 * Agreement Rate Utilities
 * Handles fetching and processing agreement rate data for evaluation forms
 */

import { QualityApi } from "@genesys/cloud-frontend/apis/QualityApi";
import {
    EvaluationSearchResponse,
    EvaluationStatusEnum,
} from "@genesys/cloud-frontend/models";
import { AgreementRateCalculator } from "./agreementRateCalculator";
import { API_CONFIGURATION } from "./apiUtils";

import { AgreementRateData } from "../features/qualityForm/qualityFormSlices/qualityFormStates";
import AutomatedScoringType from "enums/AutomatedScoringType";

// Response interface
export interface AgreementRateResponse {
    success: boolean;
    data?: AgreementRateData;
    error?: string;
}

export interface QuestionAgreementRates {
    [questionId: string]: AgreementRateData;
}

export interface QuestionAgreementRateResponse {
    success: boolean;
    data?: QuestionAgreementRates;
    error?: string;
}
// Constants for Agreement Rate Calculations
export const DEFAULT_DATE_RANGE_PERIOD = 90;
/**
 * Gets the date range
 * Used for filtering evaluations in agreement rate calculations
 *
 * @returns DateRange object with start and end dates in ISO format
 */
function getDateRange(): {
    startDate: string;
    endDate: string;
} {
    const endDate = new Date();
    const startDate = new Date();
    const dateRange = DEFAULT_DATE_RANGE_PERIOD;

    startDate.setDate(endDate.getDate() - dateRange);

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    };
}

/**
 * Creates empty agreement rate data structure
 *
 * @param formId - Published version ID (optional)
 * @returns Empty agreement rate data
 */
export function createEmptyAgreementRateData(
    formId?: string
): AgreementRateData {
    return {
        agreementRate: 0,
        acceptedSuggestions: 0,
        totalSuggestions: 0,
        hasData: false,
        formId,
    };
}

/* FORM LEVEL AGREEMENT RATE UTILS */

/**
 * Checks if the given form ID represents the latest published version
 *
 * @param formId - The form ID to check
 * @returns Promise<boolean> - true if the form is the latest published version
 */
export async function isLatestPublishedVersion(
    formId: string
): Promise<boolean> {
    try {
        const qualityApi = new QualityApi(API_CONFIGURATION);

        // Call the published forms API with the current form ID
        const publishedResponse =
            await qualityApi.getQualityPublishedformsEvaluation({
                formId: formId,
            });

        // If the response ID matches the input ID, then this is the latest published version
        return publishedResponse?.id === formId;
    } catch (error) {
        console.error(
            "Error checking if form is latest published version:",
            error
        );
        return false;
    }
}

/**
 * Builds base query filters for agreement rate searches
 * Creates common filters used across form and question-level agreement rate calculations
 *
 * @param formId - The form ID to filter by
 * @param dateRange - Date range with startDate and endDate
 * @returns Array of query filter objects
 */
function buildBaseQueryFilters(
    formId: string,
    dateRange: { startDate: string; endDate: string }
): any[] {
    return [
        {
            type: "EXACT",
            field: "evaluationStatus",
            values: [EvaluationStatusEnum.FINISHED],
        },
        {
            type: "DATE_RANGE",
            field: "submittedDate",
            startValue: dateRange.startDate,
            endValue: dateRange.endDate,
        },
        {
            type: "EXACT",
            field: "formId",
            value: formId,
        },
    ];
}

/**
 * Build form-level search request for agreement rate calculations
 * Creates a properly formatted request for form-level agreement rate data
 *
 * @param formId - The form ID to search for
 * @param dateRange - date range
 * @returns Formatted search request object
 */
function buildFormAgreementRateSearchRequest(
    formId: string,
    dateRange: any
): any {
    if (!formId) {
        throw new Error("Form ID is required for agreement rate search");
    }

    if (dateRange && (!dateRange.startDate || !dateRange.endDate)) {
        throw new Error(
            "Invalid date range provided for agreement rate search"
        );
    }

    // Build base query filters
    const queryFilters = buildBaseQueryFilters(formId, dateRange);

    return {
        pageNumber: 1,
        query: queryFilters,
        aggregations: [
            {
                name: "eaAcceptedSuggestionCount",
                field: "eaAcceptedSuggestionCount",
                type: "SUM",
            },
            {
                name: "eaSuggestionCount",
                field: "eaSuggestionCount",
                type: "SUM",
            },
            {
                name: "aiAcceptedSuggestionCount",
                field: "aiAcceptedSuggestionCount",
                type: "SUM",
            },
            {
                name: "aiSuggestionCount",
                field: "aiSuggestionCount",
                type: "SUM",
            },
        ],
    };
}

/**
 * Extracts aggregation data from API response
 *
 * @param apiResponse - API response containing aggregations
 * @returns Aggregation data or null if not found
 */
function extractFormAggregationData(apiResponse: EvaluationSearchResponse) {
    try {
        if (!apiResponse?.aggregations) {
            return null;
        }

        // Check if aggregations object has the expected properties
        const hasAggregationData =
            apiResponse.aggregations.eaAcceptedSuggestionCount !== undefined ||
            apiResponse.aggregations.eaSuggestionCount !== undefined ||
            apiResponse.aggregations.aiAcceptedSuggestionCount !== undefined ||
            apiResponse.aggregations.aiSuggestionCount !== undefined;

        if (!hasAggregationData) {
            return null;
        }

        return {
            eaAcceptedSuggestionCount:
                apiResponse.aggregations.eaAcceptedSuggestionCount,
            eaSuggestionCount: apiResponse.aggregations.eaSuggestionCount,
            aiAcceptedSuggestionCount:
                apiResponse.aggregations.aiAcceptedSuggestionCount,
            aiSuggestionCount: apiResponse.aggregations.aiSuggestionCount,
        };
    } catch (error) {
        console.error("Error extracting form aggregation data:", error);
        return null;
    }
}

/**
 * Processes the API response and calculates agreement rate data
 *
 * @param apiResponse - Raw API response from evaluation search
 * @param formId - The published version ID used for the search
 * @returns Processed agreement rate data
 */
function processFormAgreementRateResponse(
    apiResponse: EvaluationSearchResponse,
    formId: string
): AgreementRateData {
    try {
        // Extract aggregation data from API response
        const aggregationData = extractFormAggregationData(apiResponse);

        // Handle case where no aggregation data is found
        if (!aggregationData) {
            return createEmptyAgreementRateData(formId);
        }

        // Extract suggestion counts from aggregation data
        const calculationData = {
            eaAcceptedSuggestionCount:
                aggregationData.eaAcceptedSuggestionCount?.value || 0,
            eaSuggestionCount: aggregationData.eaSuggestionCount?.value || 0,
            aiAcceptedSuggestionCount:
                aggregationData.aiAcceptedSuggestionCount?.value || 0,
            aiSuggestionCount: aggregationData.aiSuggestionCount?.value || 0,
        };

        // Calculate agreement rate using the calculator utility
        const calculationResult =
            AgreementRateCalculator.calculateFormAgreementRate(calculationData);

        const finalResult = {
            agreementRate: calculationResult.agreementRate,
            acceptedSuggestions: calculationResult.acceptedSuggestions,
            totalSuggestions: calculationResult.totalSuggestions,
            hasData: calculationResult.hasData,
            formId,
        };

        return finalResult;
    } catch (error) {
        console.error("Error processing form agreement rate response:", error);
        return createEmptyAgreementRateData(formId);
    }
}

/**
 * Fetches form-level agreement rate data
 *
 * @param formId - The form ID to get agreement rate data for
 * @returns Promise resolving to agreement rate response
 */
export async function getFormAgreementRate(
    formId: string
): Promise<AgreementRateResponse> {
    try {
        // Validate input parameters
        if (!formId || typeof formId !== "string" || formId.trim() === "") {
            return {
                success: false,
                error: "Form ID is required and must be a non-empty string",
            };
        }

        const effectiveDateRange = getDateRange();

        const searchRequest = buildFormAgreementRateSearchRequest(
            formId,
            effectiveDateRange
        );

        const qualityApi = new QualityApi(API_CONFIGURATION);

        // Use QualityApi to search evaluations for agreement rate calculation
        const apiResponse = await qualityApi.postQualityEvaluationsSearch({
            body: searchRequest,
        });

        // Process API response
        const agreementRateData = processFormAgreementRateResponse(
            apiResponse,
            formId
        );

        return {
            success: true,
            data: agreementRateData,
        };
    } catch (error) {
        console.error("Error fetching form agreement rate:", error);
        return {
            success: false,
            error: "An error occurred while fetching agreement rate data",
        };
    }
}

/* QUESTION LEVEL AGREEMENT RATE UTILS */

/**
 * Identifies AI scoring and EA questions from form data
 * Filters questions to only include those with AI scoring or Evaluation Assistance enabled
 *
 * @param questions - Record of form questions
 * @returns Array of question IDs that have AI scoring or EA enabled
 */
function identifyAiScoringAndEaQuestions(
    questions: Record<string, any>
): string[] {
    if (!questions || typeof questions !== "object") {
        return [];
    }

    const aiEaQuestionIds = Object.keys(questions).filter(
        (questionId) =>
            questions[questionId].automatedScoringType !==
            AutomatedScoringType.NO_AUTOMATION
    );

    return aiEaQuestionIds;
}

/**
 * Build question-level search request for agreement rate calculations
 * Creates a properly formatted request for question-level agreement rate data
 *
 * @param questionIds - Array of question IDs to search for
 * @param options - Required parameters
 * @param options.dateRange - Date range with startDate and endDate
 * @param options.currentFormId - Published version ID to filter by
 * @returns Formatted search request object
 */
function buildQuestionAgreementRateSearchRequest(options: {
    dateRange: { startDate: string; endDate: string };
    currentFormId: string;
}): any {
    if (!options.currentFormId) {
        throw new Error(
            "Published version ID is required for question-level agreement rate search"
        );
    }

    if (
        !options.dateRange ||
        !options.dateRange.startDate ||
        !options.dateRange.endDate
    ) {
        throw new Error("Date range with startDate and endDate is required");
    }

    // Build query filters - no need to filter by questionId as aggregation will return all questions
    const queryFilters = buildBaseQueryFilters(
        options.currentFormId,
        options.dateRange
    );

    const searchRequest = {
        pageNumber: 1,
        query: queryFilters,
        aggregations: [
            {
                name: "by_question_id",
                field: "questionId",
                type: "TERM",
                subAggregations: [
                    {
                        name: "questionEaScored",
                        field: "questionEaScored",
                        type: "TERM",
                    },
                    {
                        name: "questionAiScored",
                        field: "questionAiScored",
                        type: "TERM",
                    },
                ],
            },
        ],
    };

    return searchRequest;
}

/**
 * Extracts question aggregation buckets from API response
 *
 * @param apiResponse - API response containing question-level aggregations
 * @returns Array of question aggregation buckets or empty array if not found
 */
function extractQuestionAggregationBuckets(apiResponse: any) {
    const buckets = apiResponse?.aggregations?.byQuestionId?.buckets;

    if (!Array.isArray(buckets)) {
        return [];
    }
    return buckets;
}

/**
 * Processes question aggregation bucket to extract scoring data
 *
 * @param bucket - Question aggregation bucket from API response
 * @returns QuestionAggregationData with scoring counts
 */
function processQuestionAggregationBucket(bucket: any): any {
    try {
        // Extract EA scored data - API uses 'questionEaScored' field name
        const eaScoredBuckets =
            bucket?.subAggregations?.questionEaScored?.buckets || [];

        const eaScoredTrue =
            eaScoredBuckets.find((b: any) => b.key === "true")?.documentCount ||
            0;
        const eaScoredFalse =
            eaScoredBuckets.find((b: any) => b.key === "false")
                ?.documentCount || 0;

        // Extract AI scored data - API uses 'questionAiScored' field name
        const aiScoredBuckets =
            bucket?.subAggregations?.questionAiScored?.buckets || [];

        const aiScoredTrue =
            aiScoredBuckets.find((b: any) => b.key === "true")?.documentCount ||
            0;
        const aiScoredFalse =
            aiScoredBuckets.find((b: any) => b.key === "false")
                ?.documentCount || 0;

        const result = {
            aiScoredTrue,
            aiScoredFalse,
            eaScoredTrue,
            eaScoredFalse,
        };

        return result;
    } catch (error) {
        console.error("Error processing question aggregation bucket:", error);
        return {
            aiScoredTrue: 0,
            aiScoredFalse: 0,
            eaScoredTrue: 0,
            eaScoredFalse: 0,
        };
    }
}

/**
 * Processes the API response for question-level agreement rates
 *
 * @param apiResponse - Raw API response from question-level evaluation search
 * @param questionIds - Array of current draft question IDs that were searched
 * @param publishedVersionId - The published version ID used for the search
 * @returns Processed question agreement rates data
 */
function processQuestionAgreementRateResponse(
    apiResponse: any,
    questionIds: string[],
    publishedVersionId: string
): QuestionAgreementRates {
    try {
        const questionBuckets = extractQuestionAggregationBuckets(apiResponse);

        const questionAgreementRates: QuestionAgreementRates = {};

        // Initialize all requested questions with empty data
        questionIds.forEach((questionId) => {
            questionAgreementRates[questionId] =
                createEmptyAgreementRateData(publishedVersionId);
        });

        // Process each question bucket from the API response
        questionBuckets.forEach((bucket: any) => {
            const questionId = bucket.key;

            // Only process questions that were in our original request (AI/EA questions)
            if (!questionIds.includes(questionId)) {
                return; // Skip questions that don't have AI scoring or EA
            }

            const aggregationData = processQuestionAggregationBucket(bucket);

            // Calculate agreement rate using the calculator utility
            const calculationResult =
                AgreementRateCalculator.calculateQuestionAgreementRate(
                    aggregationData
                );

            const finalData = {
                agreementRate: calculationResult.agreementRate,
                acceptedSuggestions: calculationResult.acceptedSuggestions,
                totalSuggestions: calculationResult.totalSuggestions,
                hasData: calculationResult.hasData,
                publishedVersionId,
            };

            // Store the data using the question ID
            questionAgreementRates[questionId] = finalData;
        });
        return questionAgreementRates;
    } catch (error) {
        console.error(
            "Error processing question agreement rate response:",
            error
        );

        // Return empty data for all questions on error
        const emptyResults: QuestionAgreementRates = {};
        questionIds.forEach((questionId) => {
            emptyResults[questionId] =
                createEmptyAgreementRateData(publishedVersionId);
        });
        return emptyResults;
    }
}

/**
 * Fetches question-level agreement rate data for specified questions
 *
 * @param currentFormId - The form ID to get question agreement rate data for
 * @param questions - Record of form questions to identify AI/EA questions
 * @param options - Optional parameters
 * @param options.questionIds - Specific question IDs to fetch (if not provided, will identify from questions)
 * @returns Promise resolving to question agreement rate response
 */
export async function getQuestionAgreementRates(
    currentFormId: string,
    questions: Record<string, any>,
    options: { questionIds?: string[] } = {}
): Promise<QuestionAgreementRateResponse> {
    try {
        // Validate input parameters
        if (!currentFormId) {
            return {
                success: false,
                error: "form id is required for question-level agreement rates",
            };
        }

        if (!questions || typeof questions !== "object") {
            return {
                success: false,
                error: "Questions data is required for question-level agreement rates",
            };
        }

        // Identify AI scoring and EA questions
        const questionIds =
            options.questionIds || identifyAiScoringAndEaQuestions(questions);

        // If no AI/EA questions found, return empty success response
        if (questionIds.length === 0) {
            return {
                success: true,
                data: {},
            };
        }

        // Get date range period
        const dateRange = getDateRange();

        // Build question-level search request
        const searchRequest = buildQuestionAgreementRateSearchRequest({
            dateRange,
            currentFormId,
        });

        // Use QualityApi to search evaluations for question-level agreement rate calculation
        const qualityApi = new QualityApi(API_CONFIGURATION);

        const apiResponse = await qualityApi.postQualityEvaluationsSearch({
            body: searchRequest,
        });

        // Process API response
        const questionAgreementRates = processQuestionAgreementRateResponse(
            apiResponse,
            questionIds,
            currentFormId
        );

        const result = {
            success: true,
            data: questionAgreementRates,
        };

        return result;
    } catch (error) {
        console.error("Error fetching question agreement rates:", error);

        // Handle specific error types
        if (error instanceof Error) {
            return {
                success: false,
                error: `Failed to fetch question agreement rate data: ${error.message}`,
            };
        }

        return {
            success: false,
            error: "An unexpected error occurred while fetching question agreement rate data",
        };
    }
}
